import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  QuoteEmailComposeModal,
  QuoteEmailComposeModalProps
} from 'src/content/applications/quote/QuoteEmailComposeModal';
import { normalizeEmailList } from 'src/content/applications/quote/quoteSafety';

type Props = Omit<QuoteEmailComposeModalProps, 'onSave'> & {
  data: { clientEmail?: unknown };
  onSave: (body: string, emails: string[], subject: string, bccEmails?: string[], ccEmails?: string[]) => Promise<unknown>;
  savedDeliveryNotice?: string;
  deliveryRetryBlocked?: boolean;
  duplicateDeliveryWarning?: boolean;
  onRefreshDeliveryStatus?: () => Promise<unknown>;
  onStartNewDelivery?: () => void;
  savedDeliveryDraft?: {
    body: string;
    subject: string;
    recipients: string;
  };
};
const validEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const EmailTemplateModal = ({
  data,
  onSave,
  savedDeliveryNotice = '',
  deliveryRetryBlocked = false,
  duplicateDeliveryWarning = false,
  onRefreshDeliveryStatus,
  onStartNewDelivery,
  savedDeliveryDraft,
  ...props
}: Props) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [entry, setEntry] = useState('');
  const [error, setError] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [confirmNewDelivery, setConfirmNewDelivery] = useState(false);
  useEffect(() => {
    if (!props.open) return;
    setEmails(normalizeEmailList(data?.clientEmail));
    setEntry('');
    setError(false);
  }, [props.open, data?.clientEmail]);
  useEffect(() => {
    if (!props.open || !deliveryRetryBlocked) {
      setConfirmNewDelivery(false);
      setRefreshingStatus(false);
    }
  }, [deliveryRetryBlocked, props.open]);
  const recipients = () =>
    Array.from(new Set([...emails, ...normalizeEmailList(entry)]));
  const add = () => {
    const next = recipients();
    if (next.some((email) => !validEmail(email))) {
      setError(true);
      return;
    }
    setEmails(next);
    setEntry('');
    setError(false);
  };
  return (
    <QuoteEmailComposeModal
      {...props}
      emailType="ACTUALS"
      ccSupported={true}
      recipientsEditable
      toEmails={
        deliveryRetryBlocked ? [] : recipients()
      }
      recipientHint={
        deliveryRetryBlocked
          ? 'Delivery is awaiting reconciliation. Refresh its status before sending again.'
          : 'Add at least one valid client email before sending.'
      }
      onSave={async (body, subject, bccEmails, ccEmails) => {
        if (deliveryRetryBlocked) {
          throw new Error(
            savedDeliveryNotice ||
              'Delivery is awaiting reconciliation. Do not resend yet.'
          );
        }
        const next = recipients();
        if (!next.length || next.some((email) => !validEmail(email))) {
          setError(true);
          return false;
        }
        return onSave(body, next, subject, bccEmails, ccEmails);
      }}
      recipientEditor={
        <Box>
          {savedDeliveryNotice && (
            <Alert
              severity={deliveryRetryBlocked ? 'error' : 'warning'}
              sx={{ mb: 1 }}
            >
              <strong>
                {deliveryRetryBlocked
                  ? 'Delivery outcome is unknown.'
                  : duplicateDeliveryWarning
                  ? 'Possible duplicate delivery.'
                  : 'Actuals saved; email not sent.'}
              </strong>{' '}
              {savedDeliveryNotice}
              {savedDeliveryDraft && (
                <Box component="details" sx={{ mt: 1 }}>
                  <Box component="summary" sx={{ cursor: 'pointer' }}>
                    View preserved email draft
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>To:</strong> {savedDeliveryDraft.recipients}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Subject:</strong> {savedDeliveryDraft.subject}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                  >
                    {savedDeliveryDraft.body}
                  </Typography>
                </Box>
              )}
              {deliveryRetryBlocked && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ mt: 1 }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    disabled={refreshingStatus || !onRefreshDeliveryStatus}
                    onClick={() => {
                      if (!onRefreshDeliveryStatus) return;
                      setRefreshingStatus(true);
                      void onRefreshDeliveryStatus().finally(() =>
                        setRefreshingStatus(false)
                      );
                    }}
                  >
                    {refreshingStatus
                      ? 'Refreshing status...'
                      : 'Refresh delivery status'}
                  </Button>
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    disabled={!onStartNewDelivery}
                    onClick={() => setConfirmNewDelivery(true)}
                  >
                    Start a new delivery
                  </Button>
                </Stack>
              )}
            </Alert>
          )}
          {deliveryRetryBlocked && confirmNewDelivery && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              The previous email may already have been delivered. A new
              delivery can send the same message twice. Nothing will be sent
              until you confirm here and then press Send.
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => setConfirmNewDelivery(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="warning"
                  variant="contained"
                  onClick={() => {
                    setConfirmNewDelivery(false);
                    onStartNewDelivery?.();
                  }}
                >
                  Prepare new delivery anyway
                </Button>
              </Stack>
            </Alert>
          )}
          <TextField
            fullWidth
            size="small"
            label="Send To"
            value={entry}
            error={error}
            helperText={
              error
                ? 'Enter valid email addresses.'
                : 'Press Enter or comma to add an email.'
            }
            onChange={(event) => {
              setEntry(event.target.value);
              setError(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                add();
              }
            }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 1 }}>
            {emails.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() =>
                  setEmails(emails.filter((item) => item !== email))
                }
              />
            ))}
          </Box>
        </Box>
      }
    />
  );
};
export default EmailTemplateModal;
